import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Package, X, LayoutGrid, List, SlidersHorizontal,
  ChevronLeft, ChevronRight, Loader2, AlertCircle, Filter, Printer, Plus
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import ProductGridCard from '@/components/products/ProductGridCard';
import ProductListRow from '@/components/products/ProductListRow';
import EmptyState from '@/components/shared/EmptyState';
import { toast } from 'sonner';
import { api, getBackend } from '@/api/apiClient';
import { useRepProfile } from '@/lib/RepProfileContext';

const PAGE_SIZE = 40;

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="bg-card border border-border rounded-2xl overflow-hidden animate-pulse">
          <div className="w-full h-28 bg-muted" />
          <div className="p-3 space-y-2">
            <div className="h-3 bg-muted rounded w-1/3" />
            <div className="h-4 bg-muted rounded w-full" />
            <div className="h-4 bg-muted rounded w-4/5" />
            <div className="h-5 bg-muted rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="bg-card border border-border rounded-2xl p-3 flex gap-3 animate-pulse">
          <div className="w-16 h-16 bg-muted rounded-xl flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-muted rounded w-1/4" />
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Products() {
  const navigate = useNavigate();
  const { profile: repProfile, isManager } = useRepProfile();
  // Products are added here on SellGo's own database only (on Odoo they live in Odoo).
  const canAddProduct = getBackend() === 'supabase' && (isManager || !!repProfile.allow_add_product);
  const [products, setProducts] = useState([]);
  const [imageMap, setImageMap] = useState({}); // id → data URI
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [inStockOnly, setInStockOnly] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);

  const searchTimerRef = useRef(null);
  const imageTimerRef = useRef(null);

  // Fetch categories once
  useEffect(() => {
    api.get('/products/categories').then(res => {
      if (res?.success) setCategories(res.data || []);
    });
  }, []);

  const fetchPage = useCallback(async (pg, q, stockOnly, categId, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      // Try a unique, conflict-free path first. The legacy v4 module
      // appears to register a catch-all that swallows any URL containing
      // the word "products" (returns 405 on GET), so we use a totally
      // distinct name. Fallbacks try the older variants in case the
      // SellGo addon hasn't been upgraded yet.
      const params = {
        search: q?.trim() || undefined,
        limit: PAGE_SIZE,
        page: pg,
        in_stock_only: stockOnly || undefined,
        category_id: categId || undefined,
        categ_id: categId || undefined, // legacy alias
      };
      let res;
      try {
        res = await api.get('/sellgo_inventory', params);
      } catch {
        try {
          res = await api.get('/sellgo-products', params);
        } catch {
          try {
            res = await api.get('/sellgo/products', params);
          } catch {
            res = await api.get('/products', params);
          }
        }
      }
      if (res?.success) {
        const newData = res.data || [];
        setProducts(prev => append ? [...prev, ...newData] : newData);
        setTotal(res.total || 0);
        // Lazy-load images after products render
        scheduleFetchImages(newData.map(p => p.id));
      } else {
        throw new Error(res?.error || 'فشل التحميل');
      }
    } catch (e) {
      setError(e.message);
    }
    if (append) setLoadingMore(false);
    else setLoading(false);
  }, []);

  const scheduleFetchImages = (ids) => {
    if (!ids.length) return;
    // Debounce image loading so we don't hammer the server
    clearTimeout(imageTimerRef.current);
    imageTimerRef.current = setTimeout(() => fetchImages(ids), 200);
  };

  const fetchImages = async (ids) => {
    // Only fetch ids we don't have yet
    const missing = ids.filter(id => !(id in imageMap));
    if (!missing.length) return;
    setLoadingImages(true);
    try {
      const res = await api.post('/products/images', { product_ids: missing });
      if (res?.success) {
        setImageMap(prev => ({ ...prev, ...res.data }));
      }
    } catch {}
    setLoadingImages(false);
  };

  useEffect(() => { fetchPage(0, '', false, null); }, []);

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      fetchPage(0, val, inStockOnly, selectedCategory);
    }, 350);
  };

  const handleFilter = (stockOnly, categId) => {
    setInStockOnly(stockOnly);
    setSelectedCategory(categId);
    setPage(0);
    fetchPage(0, search, stockOnly, categId);
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next, search, inStockOnly, selectedCategory, true);
  };

  const handleQuickAdd = (product) => {
    navigate(`/orders/new?product_id=${product.id}&product_name=${encodeURIComponent(product.name)}`);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasMore = page + 1 < totalPages;
  const selectedCategoryName = selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : null;

  return (
    <div className="flex flex-col h-full" dir="rtl">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border">
        {/* Title row */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-foreground">المنتجات</h1>
            <span className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1 rounded-full">
              {total.toLocaleString('ar-EG')}
            </span>
          </div>
          {/* New product + print + view toggle */}
          <div className="flex items-center gap-2">
            {canAddProduct && (
              <Button size="sm" onClick={() => navigate('/products/new')}
                className="bg-primary gap-1.5 rounded-xl shadow-lg shadow-primary/30">
                <Plus className="w-4 h-4" />
                منتج جديد
              </Button>
            )}
            <button onClick={() => navigate('/products/print/stock')}
              title="طباعة جرد المخزون"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-muted text-primary text-xs font-semibold">
              <Printer className="w-4 h-4" />
              <span className={canAddProduct ? 'hidden sm:inline' : ''}>طباعة المخزون</span>
            </button>
            <div className="flex items-center gap-1 bg-muted rounded-xl p-1">
              <button onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-card shadow text-primary' : 'text-muted-foreground'}`}>
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-card shadow text-primary' : 'text-muted-foreground'}`}>
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="بحث بالاسم، الكود، أو الباركود..."
              className="pr-10 bg-card rounded-xl border-border"
            />
            {search && (
              <button onClick={() => handleSearchChange('')} className="absolute left-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 px-4 pb-3 overflow-x-auto no-scrollbar">
          <button
            onClick={() => handleFilter(false, null)}
            className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${!inStockOnly && !selectedCategory ? 'bg-primary text-white' : 'bg-card border border-border text-muted-foreground'}`}
          >
            الكل
          </button>
          <button
            onClick={() => handleFilter(!inStockOnly, selectedCategory)}
            className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${inStockOnly ? 'bg-emerald-600 text-white' : 'bg-card border border-border text-muted-foreground'}`}
          >
            🟢 متوفر فقط
          </button>
          <button
            onClick={() => setShowCategoryFilter(true)}
            className={`flex-shrink-0 flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${selectedCategory ? 'bg-blue-600 text-white' : 'bg-card border border-border text-muted-foreground'}`}
          >
            <Filter className="w-3 h-3" />
            {selectedCategoryName || 'الفئة'}
          </button>
          {(inStockOnly || selectedCategory) && (
            <button onClick={() => handleFilter(false, null)}
              className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full bg-red-50 text-red-600 border border-red-200">
              <X className="w-3 h-3" /> مسح
            </button>
          )}
        </div>
      </div>

      {/* Category filter modal */}
      <AnimatePresence>
        {showCategoryFilter && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-end"
            onClick={() => setShowCategoryFilter(false)}
          >
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-card w-full rounded-t-3xl p-5 max-h-[70vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-foreground">اختر الفئة</h3>
                <button onClick={() => setShowCategoryFilter(false)}>
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
              <div className="overflow-y-auto flex-1 space-y-1">
                <button
                  onClick={() => { handleFilter(inStockOnly, null); setShowCategoryFilter(false); }}
                  className={`w-full text-right px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${!selectedCategory ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground'}`}
                >
                  الكل
                </button>
                {categories.map(cat => (
                  <button key={cat.id}
                    onClick={() => { handleFilter(inStockOnly, cat.id); setShowCategoryFilter(false); }}
                    className={`w-full text-right px-3 py-2.5 rounded-xl text-sm transition-colors ${selectedCategory === cat.id ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-muted text-foreground'}`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Product list */}
      <div className="flex-1 overflow-y-auto p-4 pb-8">
        {loading ? (
          viewMode === 'grid' ? <SkeletonGrid /> : <SkeletonList />
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <p className="font-semibold text-foreground">فشل التحميل</p>
            <p className="text-sm text-muted-foreground text-center max-w-xs">{error}</p>
            <Button onClick={() => fetchPage(0, search, inStockOnly, selectedCategory)} variant="outline" size="sm">
              إعادة المحاولة
            </Button>
          </div>
        ) : products.length === 0 ? (
          canAddProduct && !search && !inStockOnly && !selectedCategory ? (
            <EmptyState icon={Package} title="لا توجد منتجات بعد" description="أضف أول منتج لتبدأ البيع"
              action={
                <Button size="sm" className="bg-primary gap-1.5" onClick={() => navigate('/products/new')}>
                  <Plus className="w-4 h-4" /> منتج جديد
                </Button>
              }
            />
          ) : (
            <EmptyState icon={Package} title="لا توجد منتجات" description="لم يتم العثور على منتجات بهذه المعايير"
              action={
                <Button variant="outline" size="sm" onClick={() => handleFilter(false, null)}>مسح الفلاتر</Button>
              }
            />
          )
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {products.map((product, i) => (
                  <motion.div key={product.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.015, 0.3) }}
                  >
                    <ProductGridCard
                      product={product}
                      imageSrc={imageMap[product.id]}
                      onQuickAdd={handleQuickAdd}
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {products.map((product, i) => (
                  <motion.div key={product.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.01, 0.2) }}
                  >
                    <ProductListRow
                      product={product}
                      imageSrc={imageMap[product.id]}
                      onQuickAdd={handleQuickAdd}
                    />
                  </motion.div>
                ))}
              </div>
            )}

            {/* Load more */}
            {hasMore && (
              <div className="pt-4 pb-2">
                <Button onClick={loadMore} variant="outline" className="w-full rounded-xl" disabled={loadingMore}>
                  {loadingMore ? (
                    <><Loader2 className="w-4 h-4 animate-spin ml-2" /> جاري التحميل...</>
                  ) : (
                    `تحميل المزيد (${total - products.length} متبقي)`
                  )}
                </Button>
              </div>
            )}

            <p className="text-center text-xs text-muted-foreground pt-3 pb-4">
              {products.length.toLocaleString('ar-EG')} من {total.toLocaleString('ar-EG')} منتج
            </p>
          </>
        )}
      </div>
    </div>
  );
}